import { useEffect, useRef } from "react";
import createGlobe from "cobe";

// Cebu City - the same place the hero tagline names.
const CEBU = [10.3157, 123.8854];

// Somewhere to point the arcs: the hubs a remote engineer in Cebu actually
// works across. They double as the globe's secondary markers.
const HUBS = [
  { name: "Singapore", at: [1.3521, 103.8198] },
  { name: "Tokyo", at: [35.6762, 139.6503] },
  { name: "Sydney", at: [-33.8688, 151.2093] },
  { name: "Dubai", at: [25.2048, 55.2708] },
  { name: "London", at: [51.5074, -0.1278] },
  { name: "San Francisco", at: [37.7749, -122.4194] },
];

/* cobe v2 ships no internal render loop (its README still shows the old
   `onRender` hook, but the published build only exposes `update`, which
   applies state and redraws synchronously). So the spin is driven by the rAF
   loop below.

   Its marker shader puts a point at [-cos(lat)cos(a), sin(lat), cos(lat)sin(a)]
   with a = lon - PI, so a marker's horizontal offset is -cos(lat)cos(phi + a):
   it sits dead centre at phi = 3PI/2 - lon, and rising phi slides it to the
   right. That gives us both the opening angle and the sign of the drag below. */
const facingPhi = (lon) => (3 * Math.PI) / 2 - (lon * Math.PI) / 180;

const SPIN_PER_FRAME = 0.0025;
const DRAG_PER_PIXEL = 0.005;
const TILT_PER_PIXEL = 0.003;
const FRICTION = 0.94;
const MAX_TILT = 0.45;

// Frames to keep drawing a static globe: the dot-map texture loads async, so
// a single draw can land before there is anything to paint.
const STATIC_WARMUP_FRAMES = 90;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const Globe = ({ size = 180 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let phi = facingPhi(CEBU[1]);
    let theta = 0.25;
    let velocity = 0;
    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    let framesDrawn = 0;
    let onScreen = true;
    let frameId = null;

    const globe = createGlobe(canvas, {
      // cobe multiplies these by devicePixelRatio for the backing buffer, so
      // passing the CSS size renders at a crisp 2x rather than a wasteful 4x.
      devicePixelRatio: 2,
      width: size,
      height: size,
      phi,
      theta,
      dark: 0,
      diffuse: 1.15,
      mapSamples: 12000,
      mapBrightness: 5,
      baseColor: [0.94, 0.94, 0.96],
      markerColor: [0.93, 0.26, 0.21],
      glowColor: [1, 1, 1],
      markers: [
        { location: CEBU, size: 0.09 },
        ...HUBS.map((hub) => ({ location: hub.at, size: 0.035, color: [0.18, 0.18, 0.2] })),
      ],
      arcs: HUBS.map((hub) => ({ from: CEBU, to: hub.at })),
      arcColor: [0.93, 0.26, 0.21],
      arcWidth: 0.4,
      arcHeight: 0.35,
    });

    const onPointerDown = (event) => {
      dragging = true;
      velocity = 0;
      lastX = event.clientX;
      lastY = event.clientY;
      canvas.setPointerCapture(event.pointerId);
      canvas.classList.add("is-dragging");
    };

    const onPointerMove = (event) => {
      if (!dragging) return;
      const dx = event.clientX - lastX;
      const dy = event.clientY - lastY;
      lastX = event.clientX;
      lastY = event.clientY;

      // Rising phi slides the map right, so the globe tracks the pointer.
      phi += dx * DRAG_PER_PIXEL;
      velocity = dx * DRAG_PER_PIXEL;
      theta = clamp(theta + dy * TILT_PER_PIXEL, -MAX_TILT, MAX_TILT);
    };

    const endDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      if (canvas.hasPointerCapture(event.pointerId)) {
        canvas.releasePointerCapture(event.pointerId);
      }
      canvas.classList.remove("is-dragging");
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", endDrag);
    canvas.addEventListener("pointercancel", endDrag);

    const tick = () => {
      frameId = requestAnimationFrame(tick);
      if (!onScreen) return;

      // A reduced-motion globe holds still once the texture has painted, but
      // the loop stays alive so a deliberate drag still renders - direct
      // manipulation is the visitor's own doing, not motion imposed on them.
      const resting =
        reduceMotion && !dragging && velocity === 0 && framesDrawn > STATIC_WARMUP_FRAMES;
      if (resting) return;

      if (!dragging) {
        velocity *= FRICTION;
        if (Math.abs(velocity) < 0.00008) velocity = 0;
        phi += velocity + (reduceMotion ? 0 : SPIN_PER_FRAME);
      }

      globe.update({ phi, theta });
      framesDrawn += 1;
      if (framesDrawn === 3) canvas.classList.add("is-ready");
    };
    frameId = requestAnimationFrame(tick);

    const observer = new IntersectionObserver(
      ([entry]) => {
        onScreen = entry.isIntersecting;
      },
      { threshold: 0 }
    );
    observer.observe(canvas);

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      observer.disconnect();
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", endDrag);
      canvas.removeEventListener("pointercancel", endDrag);
      globe.destroy();
    };
  }, [size]);

  return (
    <div className="hero-globe">
      <canvas
        ref={canvasRef}
        className="hero-globe-canvas"
        style={{ width: size, height: size }}
        aria-hidden="true"
      />
      <span className="hero-globe-caption">Based in Cebu City, Philippines</span>
      <span className="hero-globe-hint">Drag to spin</span>
    </div>
  );
};
